import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Formik, FieldArray } from 'formik'
import { notification, List, Checkbox, Form, Input, Button } from 'antd'
import { createUseStyles } from 'react-jss'
import humps from 'humps'
import api from '../../api'
import { standardDate } from '../../lib/datetime'
import { getLabels } from './form-utils'

const useStyles = createUseStyles({
  listItem: {
    alignItems: 'flex-start',
  },
})

const ReportParsingIssuesForm = ({
  report,
  rawReport: { deviceReportMetadata, pdfExtraction },
  initialValues,
  onReportField,
  onSubmit,
}) => {
  const { formSections } = report
  const parsedFields = useMemo(() => {
    const labels = getLabels(formSections)
    return formSections.reduce((sum, section) => {
      section.fields.forEach(field => {
        const { itemId, itemName } = field
        const key = humps.camelize(itemName)

        if (deviceReportMetadata?.[key]) {
          sum.push({
            itemId,
            key,
            value:
              field.dataType === 'date'
                ? standardDate(deviceReportMetadata[key])
                : deviceReportMetadata[key],
            label: labels[itemId],
          })
        }
      })
      return sum
    }, [])
  }, [deviceReportMetadata, formSections])

  const onFormSubmit = async (payload, actions) => {
    try {
      await api.transmissions.reportExtractionError(pdfExtraction.id, payload)
      notification.success({
        message: 'Report sent',
        description:
          'Thanks for your feedback, it will help us improve our autofill engine.',
      })
      actions.resetForm()
      onSubmit(payload)
    } catch (error) {
      notification.error({
        message: 'Error sending report, please try again.',
        description: error.message,
      })
    }
  }

  const classes = useStyles()
  return (
    <div className="ReportParsingIssuesForm">
      <p>Please select any fields that does not match the value in the PDF:</p>
      <Formik
        initialValues={initialValues}
        onSubmit={onFormSubmit}
        enableReinitialize
      >
        {({ values, handleChange, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <FieldArray name="errorFields">
              {() => (
                <List
                  dataSource={parsedFields}
                  size="small"
                  bordered
                  renderItem={item => {
                    const { key, itemId, label, value } = item
                    return (
                      <List.Item key={itemId} className={classes.listItem}>
                        <Checkbox
                          value={key}
                          checked={values.errorFields.includes(key)}
                          onChange={e => {
                            onReportField({ itemName: item.key })
                          }}
                          style={{ marginRight: 10 }}
                        />
                        <List.Item.Meta title={label} description={value} />
                      </List.Item>
                    )
                  }}
                />
              )}
            </FieldArray>

            <div style={{ marginTop: 16 }}>
              <Form.Item label="Comments">
                <Input.TextArea
                  id="observations"
                  name="observations"
                  value={values.observations}
                  onChange={handleChange}
                  rows={4}
                />
              </Form.Item>
            </div>

            <Button type="primary" htmlType="submit" style={{ marginTop: 16 }}>
              Send
            </Button>
          </form>
        )}
      </Formik>
    </div>
  )
}

export default ReportParsingIssuesForm

ReportParsingIssuesForm.propTypes = {
  report: PropTypes.shape({
    id: PropTypes.string,
    formSections: PropTypes.array,
    fieldValues: PropTypes.object,
  }).isRequired,
  rawReport: PropTypes.shape({
    deviceReportMetadata: PropTypes.shape(),
    pdfExtraction: PropTypes.shape({
      id: PropTypes.string,
      status: PropTypes.string,
    }),
  }),
  initialValues: PropTypes.shape({
    errorFields: PropTypes.arrayOf(PropTypes.string),
    observations: PropTypes.string,
  }),
  onSubmit: PropTypes.func,
  onReportField: PropTypes.func,
}

ReportParsingIssuesForm.defaultProps = {
  onSubmit: () => {},
  onReportField: () => {},
  rawReport: {
    deviceReportMetadata: {},
    pdfExtraction: {
      id: '',
      status: '',
    },
  },
  initialValues: {
    errorFields: [],
    observations: '',
  },
}
